/**
 * Whether the current URL is one of this app's hand-rolled routes at all.
 *
 * There is no router here — `deep-link.ts`, `news-route.ts` and
 * `admin-route.ts` each make that case for their own path, and this is the
 * same shape at the top: a pure predicate over a pathname, plus the one
 * impure line that reads `window.location`. It exists because none of those
 * three parsers, nor the plain `/`, ever answered "this URL is wrong" —
 * `parseDeepLink` and `parseNewsRoute` both fall back to the app opening
 * normally on anything they don't recognise (see their own comments on why
 * "absent is not broken"), and `parseAdminRoute` falls back to `overview`
 * for the same reason. A stranger typing `/scoreboard` or a link with a typo
 * in it got the ordinary sign-in page with no indication anything was wrong.
 *
 * Listed by top path segment rather than matched exactly, because `/admin`
 * and `/news` both have sub-paths of their own that this must not shadow —
 * their own parsers already decide what to do with whatever comes after.
 * `win`/`defeat` are the dev-only result-screen preview
 * (`worstCaseResultHud` in `App.vue`) — kept out of "not found" even in a
 * production build, where they are already inert rather than broken, so
 * this does not change what a stranger sees there.
 */
const KNOWN_TOP_SEGMENTS = new Set([
  'admin',
  'news',
  'verify-email',
  'reset-password',
  'win',
  'defeat',
]);

export function isKnownPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return true;
  const first = path.split('/')[1] ?? '';
  return KNOWN_TOP_SEGMENTS.has(first);
}

/** Reads the live URL. The one impure line, kept out of the predicate. */
export function readIsNotFound(): boolean {
  if (typeof window === 'undefined') return false;
  return !isKnownPath(window.location.pathname);
}
